import { Database, Tables } from "@punchup/data/supabase.types";
import { SentryFeatureTags, logException } from "@punchup/sentry";

import { SupabaseClient } from "@supabase/supabase-js";

export type VenuePageNavGroupRow = Tables<"venue_pages_nav_groups">;

export async function getVenuePageNavGroups(
  supabase: SupabaseClient<Database>,
  venuePageId: string
): Promise<VenuePageNavGroupRow[]> {
  try {
    const { data } = await supabase
      .from("venue_pages_nav_groups")
      .select("*")
      .eq("venue_page_id", venuePageId)
      .throwOnError();

    return data ?? [];
  } catch (error) {
    logException(error, {
      extra: { data: "getVenuePageNavGroups", venuePageId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    // Degrade to no groups on any failure so the venue page nav still renders.
    return [];
  }
}

export async function createVenuePageNavGroup({
  supabase,
  venuePageId,
  label,
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  label: string;
}): Promise<VenuePageNavGroupRow> {
  const { data, error } = await supabase
    .from("venue_pages_nav_groups")
    .insert({ venue_page_id: venuePageId, label })
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "createVenuePageNavGroup", venuePageId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function updateVenuePageNavGroup({
  supabase,
  groupId,
  label,
}: {
  supabase: SupabaseClient<Database>;
  groupId: string;
  label: string;
}): Promise<VenuePageNavGroupRow> {
  const { data, error } = await supabase
    .from("venue_pages_nav_groups")
    .update({ label })
    .eq("id", groupId)
    .select()
    .single();

  if (error) {
    logException(error, {
      extra: { data: "updateVenuePageNavGroup", groupId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }

  return data;
}

export async function deleteVenuePageNavGroup({
  supabase,
  groupId,
}: {
  supabase: SupabaseClient<Database>;
  groupId: string;
}): Promise<void> {
  // Deleting the group fires the `venue_pages_nav_groups_after_delete`
  // trigger, which removes the matching `venue_pages_nav_items` row
  // (item_type='group', item_key=group.id) in the same transaction. Child
  // nav_items rows have their parent_group_id reset to null via the FK's
  // ON DELETE SET NULL.
  const { error } = await supabase
    .from("venue_pages_nav_groups")
    .delete()
    .eq("id", groupId);

  if (error) {
    logException(error, {
      extra: { data: "deleteVenuePageNavGroup", groupId },
      tags: { supabase: true, feature: SentryFeatureTags.VenuePages },
    });
    throw error;
  }
}
